import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { IonPage, IonContent, useIonRouter } from '@ionic/react';
import { useLocation } from 'react-router-dom';
import { getClientById, setClientDemo, unsetClientDemo, getClientTeamPlan, setClientInactive, setClientActive, deleteClient, updateClient } from '../services/clientService';
import { searchBusinesses, updateShopStartDate, updateCollisionWindow, updateAgentMode, updatePlanStatus, updateOnboardingProgress, completeOnboarding } from '../services/businessService';
import { showInfo } from '../services/toastService';
import { searchAIAgents } from '../services/aiAgentService';
import { useBreadcrumbStore } from '../store/useBreadcrumbStore';
import { useRefresh } from '../contexts/RefreshContext';
import { CallList } from '../components/calls/CallList';
import { PropertyList } from '../components/properties/PropertyList';
import { TeamList } from '../components/teams/TeamList';
import { ClientCertificationList } from '../components/clients/ClientCertificationList';
import { AgentList } from '../components/agents/AgentList';
import { AddClientModal } from '../components/shared/AddClientModal';
import { AddClientAdminModal } from '../components/shared/AddClientAdminModal';
import { ManageClientAdminsModal } from '../components/clients/ManageClientAdminsModal';
import { ShopsBudget } from '../components/shared/ShopsBudget';
import { EditScoreCategoriesModal } from '../components/shared/EditScoreCategoriesModal';
import type { OnboardPrefill } from '../components/shared/AddClientModal';
import type { Business, BusinessActivePlan } from '../types/business';
import type { ClientAdminSummary, ClientStatus } from '../types/client';
import type { RetellAIAgent } from '../types/aiAgent';
import { useProfileStore } from '../store/useProfileStore';
import { canManageBusinessProfiles } from '../types/profile';

export default function ClientDetailPage() {
  const location = useLocation();
  const router = useIonRouter();
  const params = new URLSearchParams(location.search);
  const clientId = params.get('clientId') || undefined;

  const [clientName, setClientName] = useState('');
  const [clientStatus, setClientStatus] = useState<ClientStatus>('active');
  const [isDemo, setIsDemo] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [planBusiness, setPlanBusiness] = useState<Business | null>(null);
  const [activePlan, setActivePlan] = useState<BusinessActivePlan | null>(null);
  const [clientAdmins, setClientAdmins] = useState<ClientAdminSummary[]>([]);
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerDisplayName, setOwnerDisplayName] = useState('');
  const [ownerProfileId, setOwnerProfileId] = useState<string | null>(null);
  const [ownerInvitationAccepted, setOwnerInvitationAccepted] = useState(false);
  const [agents, setAgents] = useState<RetellAIAgent[]>([]);
  const [showScoreCategories, setShowScoreCategories] = useState(false);
  const [editPrefill, setEditPrefill] = useState<OnboardPrefill | null>(null);
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [showManageAdminsModal, setShowManageAdminsModal] = useState(false);
  const [showEditScoreCategories, setShowEditScoreCategories] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showStartDateModal, setShowStartDateModal] = useState(false);
  const [startDateInput, setStartDateInput] = useState('');
  const [showStripeLink, setShowStripeLink] = useState(false);
  const [showScoringNote, setShowScoringNote] = useState<string | null>(null);
  const [editingClientName, setEditingClientName] = useState(false);
  const [clientNameInput, setClientNameInput] = useState('');
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const statusMenuRef = useRef<HTMLDivElement>(null);
  const [showCollisionWindowModal, setShowCollisionWindowModal] = useState(false);
  const [collisionWindowInput, setCollisionWindowInput] = useState(90);
  const [showAgentModeModal, setShowAgentModeModal] = useState(false);
  const [agentModeInput, setAgentModeInput] = useState('');
  const [stripeLinkCopied, setStripeLinkCopied] = useState(false);
  const [shopsTotal, setShopsTotal] = useState<number | null>(null);
  const [shopsPlaced, setShopsPlaced] = useState<number | null>(null);
  const [shopsScheduled, setShopsScheduled] = useState<number | null>(null);
  const [billingStartDate, setBillingStartDate] = useState<string | null>(null);
  const [billingEndDate, setBillingEndDate] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showOnboardingConfirm, setShowOnboardingConfirm] = useState(false);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const setBreadcrumb = useBreadcrumbStore((s) => s.set);
  const { triggerRefresh } = useRefresh();
  const { activeProfile } = useProfileStore();
  const canManageProfiles = canManageBusinessProfiles(activeProfile);

  const fetchClientData = useCallback(() => {
    if (!clientId) {
      setBreadcrumb([{ label: 'Clients', path: '/clients' }]);
      return;
    }
    getClientById(clientId).then((client) => {
      const name = client.name || clientId;
      setClientName(name);
      setIsDemo(!!client.demo);
      setClientStatus(client.status || 'active');
      setShopsTotal(client.shopsTotal ?? null);
      setShopsPlaced(client.shopsPlaced ?? null);
      setShopsScheduled(client.shopsScheduled ?? null);
      setBillingStartDate(client.billingStartDate ?? null);
      setBillingEndDate(client.billingEndDate ?? null);
      setBreadcrumb([
        { label: 'Clients', path: '/clients' },
        { label: name },
      ]);
    });
  }, [clientId, setBreadcrumb]);

  useEffect(() => {
    if (location.pathname !== '/client-detail') return;
    fetchClientData();
  }, [location.pathname, fetchClientData]);

  // Click-outside to close menu
  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  useEffect(() => {
    if (!statusMenuOpen) return;
    function handleClick(e: MouseEvent) {
      if (statusMenuRef.current && !statusMenuRef.current.contains(e.target as Node)) setStatusMenuOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [statusMenuOpen]);

  const handleToggleDemo = async () => {
    if (!clientId) return;
    setMenuOpen(false);
    if (isDemo) {
      await unsetClientDemo(clientId);
    } else {
      await setClientDemo(clientId);
    }
    setIsDemo(!isDemo);
  };

  const handleToggleStatus = async () => {
    if (!clientId) return;
    setMenuOpen(false);
    if (clientStatus === 'active') {
      await setClientInactive(clientId);
      setClientStatus('inactive');
    } else {
      await setClientActive(clientId);
      setClientStatus('active');
    }
  };

  const handleSaveClientName = async () => {
    if (!clientId || !clientNameInput.trim()) return;
    await updateClient(clientId, { name: clientNameInput.trim() });
    setClientName(clientNameInput.trim());
    setEditingClientName(false);
    setBreadcrumb([{ label: 'Clients', path: '/clients' }, { label: clientNameInput.trim() }]);
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!planBusiness) return;
    setStatusMenuOpen(false);
    await updatePlanStatus(planBusiness.id, newStatus);
    fetchPlanData();
  };

  const handleDelete = async () => {
    if (!clientId) return;
    setDeleteLoading(true);
    try {
      await deleteClient(clientId);
      setShowDeleteConfirm(false);
      router.push('/clients', 'back');
    } catch {
      // error handled by apiService
    } finally {
      setDeleteLoading(false);
    }
  };

  const fetchPlanData = useCallback(() => {
    if (!clientId) return;
    Promise.all([
      getClientTeamPlan(clientId),
      searchAIAgents({ active: true }, { pageSize: 1000, orderBy: 'name', order: 'asc' }),
    ]).then(([teamPlanResp, ags]) => {
      setAgents(ags);
      if (!teamPlanResp) {
        showInfo('Client has no team plan.');
        setPlanBusiness(null);
        setActivePlan(null);
        return;
      }
      const biz = teamPlanResp.business || null;
      setPlanBusiness(biz);
      setActivePlan(teamPlanResp.activePlan || null);
      setClientAdmins(teamPlanResp.clientAdmins || []);
      // Derive owner from client_owner profile
      const ownerProfile = biz?.profiles?.find(p => p.roles?.includes('client_owner'));
      setOwnerEmail(ownerProfile?.email || '');
      setOwnerDisplayName(ownerProfile?.displayName || '');
      setOwnerProfileId(ownerProfile?.id || null);
      setOwnerInvitationAccepted(!!ownerProfile?.invitationAcceptedAt);
    }).catch(() => {});
  }, [clientId]);

  useEffect(() => { fetchPlanData(); }, [fetchPlanData]);

  const handleRefresh = useCallback(() => {
    if (!clientId) return;
    fetchClientData();
    fetchPlanData();
    triggerRefresh(`/properties-${clientId}`);
    triggerRefresh(`/teams-${clientId}`);
    triggerRefresh(`/agents-${clientId}`);
    triggerRefresh('/calls');
  }, [clientId, fetchClientData, fetchPlanData, triggerRefresh]);

  const SHOP_PLAN_LABELS: Record<string, string> = {
    basic: 'Single Shop',
    starter: 'Starter',
    pro: 'Pro',
    custom: 'Custom',
  };

  const agentName = activePlan?.agentId
    ? agents.find((a) => a.retellAgentId === activePlan.agentId)?.name || activePlan.agentId
    : null;

  const handleEdit = async () => {
    if (!planBusiness || !activePlan) return;
    setEditPrefill({
      email: ownerEmail,
      displayName: ownerDisplayName,
      clientName: clientName,
      teamName: planBusiness.name || '',
      totalSeats: activePlan.totalSeats || 0,
      mysteryShopPlan: activePlan.mysteryShopPlan || null,
      shopsMonthly: activePlan.shopsMonthly ?? null,
      agentId: activePlan.agentId || null,
      demo: planBusiness.demo || false,
      profileId: ownerProfileId,
      businessId: planBusiness.id || null,
      clientId: planBusiness.clientId || null,
      invitationAccepted: ownerInvitationAccepted,
    });
  };

  // Determine if delete should be disabled (non-demo with data)
  const [hasData, setHasData] = useState(false);
  useEffect(() => {
    if (!clientId) return;
    searchBusinesses({ clientId }, { pageSize: 1 }).then((results) => {
      setHasData(results.length > 0);
    }).catch(() => {});
  }, [clientId]);
  const deleteDisabled = !isDemo && hasData;

  const callFilter = useMemo(() => ({ clientId }), [clientId]);

  const handleAudit = () => {
    setMenuOpen(false);
    router.push(`/client-audit?clientId=${clientId}`, 'none');
  };

  if (!clientId) return null;

  return (
    <IonPage>
      <IonContent>
        <div className="space-y-2">
          <div className="px-4">
          {/* Client Info & Plan Section */}
            <div className="p-4 sm:p-4 lg:p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  {editingClientName ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={clientNameInput}
                        onChange={(e) => setClientNameInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveClientName(); if (e.key === 'Escape') setEditingClientName(false); }}
                        autoFocus
                        className="text-lg font-bold text-gray-900 border border-gray-300 rounded px-2 py-0.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button onClick={handleSaveClientName} className="text-blue-600 hover:text-blue-800 text-sm font-medium">Save</button>
                      <button onClick={() => setEditingClientName(false)} className="text-gray-400 hover:text-gray-600 text-sm">Cancel</button>
                    </div>
                  ) : (
                    <>
                      <h1 className="text-lg font-bold text-gray-900">{clientName}</h1>
                      <button onClick={() => { setClientNameInput(clientName); setEditingClientName(true); }} className="p-1 text-gray-400 hover:text-gray-700 transition-colors" title="Edit client name">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      {isDemo && (
                        <span className="text-sm font-medium text-gray-500">DEMO</span>
                      )}
                    </>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={handleRefresh}
                    className="p-2 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                    title="Refresh"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 2v6h-6"/>
                      <path d="M3 12a9 9 0 0 1 15-6.7L21 8"/>
                      <path d="M3 22v-6h6"/>
                      <path d="M21 12a9 9 0 0 1-15 6.7L3 16"/>
                    </svg>
                  </button>
                  {!activePlan && (
                    <div ref={menuRef} className="relative">
                      <button onClick={() => setMenuOpen(!menuOpen)} className="p-1 text-gray-400 hover:text-gray-700 rounded hover:bg-gray-100 transition-colors">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="19" r="2" /></svg>
                      </button>
                      {menuOpen && (
                        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[180px]">
                          <button onClick={handleAudit} className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">Client Audit</button>
                          <button onClick={handleToggleDemo} className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">{isDemo ? 'Unset Demo' : 'Set as Demo Account'}</button>
                          <button onClick={handleToggleStatus} className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">{clientStatus === 'inactive' ? 'Set Active' : 'Set Inactive'}</button>
                          <button onClick={() => { setMenuOpen(false); setShowDeleteConfirm(true); }} className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-gray-50">Delete Client</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {activePlan && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <h2 className="text-sm font-semibold text-gray-700">Plan</h2>
                      {activePlan.status && (
                        <div ref={statusMenuRef} className="relative">
                          <button
                            type="button"
                            onClick={() => setStatusMenuOpen(!statusMenuOpen)}
                            className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full cursor-pointer transition-colors ${
                              ({
                                active: 'bg-green-100 text-green-800 hover:bg-green-200',
                                pending: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200',
                                trialing: 'bg-gray-100 text-gray-800 hover:bg-gray-200',
                                paused: 'bg-orange-100 text-orange-800 hover:bg-orange-200',
                                cancelled: 'bg-red-100 text-red-800 hover:bg-red-200',
                                expired: 'bg-red-100 text-red-800',
                                ended: 'bg-red-100 text-red-800',
                              } as Record<string, string>)[activePlan.status] || 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {activePlan.status.charAt(0).toUpperCase() + activePlan.status.slice(1)}
                          </button>
                          {statusMenuOpen && (
                            <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[140px]">
                              {activePlan.status === 'active' && (
                                <>
                                  <button onClick={() => handleStatusChange('paused')} className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">Pause</button>
                                  <button onClick={() => handleStatusChange('cancelled')} className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
                                </>
                              )}
                              {(activePlan.status === 'paused' || activePlan.status === 'cancelled') && (
                                <>
                                  <button onClick={() => handleStatusChange('pending')} className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">Set Pending</button>
                                  <button onClick={() => handleStatusChange('active')} className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">Set Active</button>
                                </>
                              )}
                              {activePlan.status === 'pending' && (
                                <>
                                  <button onClick={() => handleStatusChange('active')} className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">Set Active</button>
                                  <button onClick={() => { setStatusMenuOpen(false); setStripeLinkCopied(false); setShowStripeLink(true); }} className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">Redirect Link</button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={handleEdit} className="px-3 py-1 text-xs font-medium text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors">Edit Plan</button>
                      <div ref={menuRef} className="relative">
                        <button onClick={() => setMenuOpen(!menuOpen)} className="p-1 text-gray-400 hover:text-gray-700 rounded hover:bg-gray-100 transition-colors">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="19" r="2" /></svg>
                        </button>
                        {menuOpen && (
                          <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[180px]">
                            <button onClick={handleAudit} className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">Client Audit</button>
                            <button onClick={() => { setMenuOpen(false); setShowAddAdminModal(true); }} className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">Add Client Admin</button>
                            {canManageProfiles && (
                              <button onClick={() => { setMenuOpen(false); setShowManageAdminsModal(true); }} className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">Manage Client Admins</button>
                            )}
                            <button onClick={handleToggleDemo} className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">{isDemo ? 'Unset Demo' : 'Set as Demo Account'}</button>
                            <button onClick={handleToggleStatus} className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">{clientStatus === 'inactive' ? 'Set Active' : 'Set Inactive'}</button>
                            <button onClick={() => { setMenuOpen(false); setShowDeleteConfirm(true); }} disabled={deleteDisabled} className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 ${deleteDisabled ? 'text-gray-300 cursor-not-allowed' : 'text-red-600'}`}>Delete Client</button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {ownerDisplayName && (
                    <p className="text-sm mb-1"><span className="font-medium text-gray-500">ACCOUNT OWNER:</span> {ownerDisplayName}{ownerEmail ? ` (${ownerEmail})` : ''}</p>
                  )}
                  <p className="text-sm mb-3"><span className="font-medium text-gray-500">ACCOUNT ADMINS:</span>{' '}
                    {clientAdmins.length > 0
                      ? clientAdmins.map((a) => `${a.displayName} (${a.email})`).join(', ')
                      : '—'}
                  </p>
                  <div className="flex gap-4">
                    {/* LMS */}
                    <div className="border border-gray-200 rounded-lg p-4 space-y-1 w-[200px] shrink-0">
                      <p className="text-sm font-medium text-gray-800 mb-2">LMS</p>
                      <p className="text-sm"><span className="font-medium text-gray-500">SEATS:</span> {activePlan.totalSeats}</p>
                      {activePlan.seatPriceMonthly != null && (
                        <p className="text-sm"><span className="font-medium text-gray-500">PRICE/MO:</span> ${activePlan.seatPriceMonthly}</p>
                      )}
                      {activePlan.seatPriceAnnual != null && (
                        <p className="text-sm"><span className="font-medium text-gray-500">PRICE/YR:</span> ${activePlan.seatPriceAnnual}</p>
                      )}
                    </div>

                    {/* Mystery Shop */}
                    <div className="border border-gray-200 rounded-lg p-4 space-y-1 flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <p className="text-sm font-medium text-gray-800">Mystery Shop</p>
                        {activePlan.mysteryShopPlan && !activePlan.onboardingCompletedAt && (
                          <button
                            type="button"
                            onClick={() => setShowOnboardingConfirm(true)}
                            className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800 hover:bg-red-200 cursor-pointer transition-colors"
                          >
                            Onboarding Step {(activePlan.onboardingProgress ?? 0) + 1} Incomplete
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-0.5">
                        {/* Column 1 */}
                        <div className="space-y-0.5">
                          {billingStartDate && billingEndDate && (
                            <p className="text-sm"><span className="font-medium text-gray-500">BILLING PERIOD:</span> {(() => {
                              const fmt = (d: string) => { const dt = new Date(d + 'T00:00:00'); return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); };
                              const endYear = new Date(billingEndDate + 'T00:00:00').getFullYear();
                              return `${fmt(billingStartDate)} – ${fmt(billingEndDate)}, ${endYear}`;
                            })()}</p>
                          )}
                          {shopsTotal != null && shopsPlaced != null && (
                            <p className="text-sm"><ShopsBudget shopsPlaced={shopsPlaced} shopsScheduled={shopsScheduled ?? 0} shopsTotal={shopsTotal} /></p>
                          )}
                          <p className="text-sm"><span className="font-medium text-gray-500">PLAN:</span> {activePlan.mysteryShopPlan ? (SHOP_PLAN_LABELS[activePlan.mysteryShopPlan] || activePlan.mysteryShopPlan) : '—'}</p>
                          {activePlan.shopsMonthly != null && (
                            <p className="text-sm"><span className="font-medium text-gray-500">SHOPS/MO:</span> {activePlan.shopsMonthly}</p>
                          )}
                          {activePlan.mysteryShopPlan && activePlan.scoreCategories && activePlan.scoreCategories.length > 0 && (
                            <p className="text-sm">
                              <button type="button" onClick={() => setShowScoreCategories(true)} className="text-blue-600 hover:text-blue-800 hover:underline">Score Categories</button>
                            </p>
                          )}
                        </div>
                        {/* Column 2 */}
                        <div className="space-y-0.5">
                          <p className="text-sm"><span className="font-medium text-gray-500">START DATE:</span>{' '}
                            <button type="button" onClick={() => { setStartDateInput(activePlan.shopStartDate || ''); setShowStartDateModal(true); }} className="text-blue-600 hover:text-blue-800 hover:underline">
                              {activePlan.shopStartDate ? (() => { const [y, m, d] = activePlan.shopStartDate.split('-'); return `${m}/${d}/${y}`; })() : '—'}
                            </button>
                          </p>
                          {agentName && (
                            <p className="text-sm"><span className="font-medium text-gray-500">AI AGENT:</span> {agentName}</p>
                          )}
                          <p className="text-sm"><span className="font-medium text-gray-500">COLLISION DAYS:</span>{' '}
                            <button type="button" onClick={() => { setCollisionWindowInput(activePlan.phoneAliasCollisionWindow ?? 90); setShowCollisionWindowModal(true); }} className="text-blue-600 hover:text-blue-800 hover:underline">
                              {activePlan.phoneAliasCollisionWindow ?? 90}
                            </button>
                          </p>
                          <p className="text-sm"><span className="font-medium text-gray-500">AGENT MODE:</span>{' '}
                            <button type="button" onClick={() => { setAgentModeInput(activePlan.agentMode || 'property'); setShowAgentModeModal(true); }} className="text-blue-600 hover:text-blue-800 hover:underline">
                              {activePlan.agentMode === 'client' ? 'Client-wide' : 'Per Property'}
                            </button>
                          </p>
                          {!!(activePlan.metadata as Record<string, unknown>)?.clientPlatform && (
                            <p className="text-sm"><span className="font-medium text-gray-500">CLIENT PLATFORM:</span> {String((activePlan.metadata as Record<string, unknown>).clientPlatform)}</p>
                          )}
                          {!!(activePlan.metadata as Record<string, unknown>)?.scoringSuggestion && (() => {
                            const full = String((activePlan.metadata as Record<string, unknown>).scoringSuggestion);
                            const clamped = full.length > 25 ? full.slice(0, 25) + '…' : full;
                            return (
                              <p className="text-sm">
                                <span className="font-medium text-gray-500">SCORING SUGGESTION:</span>{' '}
                                <span
                                  className="cursor-pointer"
                                  onClick={() => { setShowScoringNote(full); }}
                                  title={full}
                                >
                                  {clamped}
                                </span>
                              </p>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Calls Section */}
            <div className="p-4 sm:p-4 lg:p-4">
              <CallList title={clientName ? `Calls - ${clientName}` : 'Calls'} filter={callFilter} />
            </div>
          </div>

          <PropertyList title={clientName ? `Properties - ${clientName}` : 'Properties'} clientId={clientId} />
          {clientId && (
            <ClientCertificationList
              clientId={clientId}
              title={clientName ? `Certifications - ${clientName}` : 'Certifications'}
            />
          )}
          <TeamList title={clientName ? `Teams - ${clientName}` : 'Teams'} clientId={clientId} primaryBusinessId={planBusiness?.id} />
          <AgentList title={clientName ? `Agents - ${clientName}` : 'Agents'} clientId={clientId} />
        </div>

        {/* Score Categories Modal (read-only, edit-style view) */}
        {showScoreCategories && activePlan?.scoreCategories && (() => {
          const CATEGORY_COLORS = ['#1D9E75', '#185FA5', '#BA7517', '#993556', '#533489'];
          const cats = activePlan.scoreCategories;
          return (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowScoreCategories(false)} />
            <div className="relative bg-white rounded-xl shadow-lg w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
              <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-900">Score Categories</h2>
                  <button
                    type="button"
                    onClick={() => { setShowScoreCategories(false); setShowEditScoreCategories(true); }}
                    className="px-3 py-1 text-xs font-medium text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    Edit
                  </button>
                </div>

                {/* Category weights */}
                <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-3">Category weights</div>
                <div className="border border-gray-200 rounded-xl p-5 bg-[#f9fafb] mb-6">
                  {/* Stacked bar */}
                  <div className="h-2.5 rounded-md overflow-hidden flex gap-0.5 mb-5">
                    {cats.map((cat, i) => (
                      <div key={i} className="h-full rounded-sm" style={{ width: `${cat.weight}%`, background: CATEGORY_COLORS[i % CATEGORY_COLORS.length] }} />
                    ))}
                  </div>
                  {/* Weight labels */}
                  {cats.map((cat, i) => {
                    const color = CATEGORY_COLORS[i % CATEGORY_COLORS.length];
                    return (
                      <div key={i} className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 text-[13px]">
                          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
                          <span>{cat.title}</span>
                        </div>
                        <span className="text-sm font-semibold min-w-[36px] text-right" style={{ color }}>{cat.weight}%</span>
                      </div>
                    );
                  })}
                </div>

                {/* Scoring criteria */}
                <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-3">Scoring criteria</div>
                {cats.map((cat, ci) => {
                  if (cat.highlighted) return null;
                  if (!cat.criteria?.length) return null;
                  const activeCount = cat.criteria.filter(c => !c.disabled).length;
                  const color = CATEGORY_COLORS[ci % CATEGORY_COLORS.length];
                  return (
                    <div key={ci} className="border border-gray-200 rounded-xl mb-3 overflow-hidden bg-white">
                      <div className="flex items-center justify-between w-full px-4 py-3 bg-[#f9fafb] border-b border-gray-200">
                        <div className="flex items-center gap-2.5">
                          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
                          <span className="text-[13px] font-semibold">{cat.title}</span>
                          <span className="text-[11px] text-gray-400">{activeCount}/{cat.criteria.length} active</span>
                        </div>
                      </div>
                      <div className="py-2">
                        {cat.criteria.map((crit, cri) => (
                          <div key={cri} className="flex items-center justify-between px-4 py-2 border-b border-gray-200 last:border-b-0">
                            <span className={`text-[13px] flex-1 ${crit.disabled ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                              {crit.text}
                            </span>
                            <span className={`text-[11px] font-medium ${crit.disabled ? 'text-gray-400' : 'text-[#1D9E75]'}`}>
                              {crit.disabled ? 'OFF' : 'ON'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-end px-6 py-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowScoreCategories(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
          );
        })()}
        {editPrefill && (
          <AddClientModal
            prefill={editPrefill}
            onClose={() => setEditPrefill(null)}
            onSuccess={() => { setEditPrefill(null); fetchPlanData(); }}
          />
        )}
        {showEditScoreCategories && planBusiness && activePlan?.scoreCategories && (
          <EditScoreCategoriesModal
            teamId={planBusiness.id}
            scoreCategories={activePlan.scoreCategories}
            onClose={() => setShowEditScoreCategories(false)}
            onSuccess={() => { setShowEditScoreCategories(false); fetchPlanData(); }}
          />
        )}
        {showAddAdminModal && planBusiness && (
          <AddClientAdminModal
            businessId={planBusiness.id}
            onClose={() => setShowAddAdminModal(false)}
            onSuccess={() => { setShowAddAdminModal(false); fetchPlanData(); }}
          />
        )}
        {showManageAdminsModal && planBusiness && (
          <ManageClientAdminsModal
            businessId={planBusiness.id}
            profiles={planBusiness.profiles || []}
            onClose={() => setShowManageAdminsModal(false)}
            onSuccess={fetchPlanData}
            onAddAdmin={() => { setShowManageAdminsModal(false); setShowAddAdminModal(true); }}
          />
        )}
        {showOnboardingConfirm && planBusiness && activePlan && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowOnboardingConfirm(false)} />
            <div className="relative bg-white rounded-xl shadow-lg w-full max-w-sm mx-4">
              <div className="px-6 pt-6 pb-4">
                <h2 className="text-lg font-bold text-gray-900 mb-2">Mark Onboarding Completed?</h2>
                <p className="text-sm text-gray-600">This will mark the onboarding as completed for this client.</p>
              </div>
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowOnboardingConfirm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={onboardingLoading}
                  onClick={async () => {
                    setOnboardingLoading(true);
                    try {
                      await updateOnboardingProgress(planBusiness.id, activePlan.onboardingProgress ?? 0);
                      await completeOnboarding(planBusiness.id, {
                        clientPlatform: (activePlan.metadata as Record<string, unknown>)?.clientPlatform as string | undefined,
                        agentMode: activePlan.agentMode || undefined,
                      });
                      setShowOnboardingConfirm(false);
                      fetchPlanData();
                    } catch {
                      // error handled by apiService
                    } finally {
                      setOnboardingLoading(false);
                    }
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {onboardingLoading ? 'Completing...' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        )}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowDeleteConfirm(false)} />
            <div className="relative bg-white rounded-xl shadow-lg w-full max-w-sm mx-4">
              <div className="px-6 pt-6 pb-4">
                <h2 className="text-lg font-bold text-gray-900 mb-2">Delete Client</h2>
                <p className="text-sm text-gray-600">Are you sure you want to delete this client? This will also delete all associated teams, properties, agents, and calls.</p>
              </div>
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={deleteLoading}
                  onClick={handleDelete}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {deleteLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
        {showStripeLink && activePlan?.stripeRedirectLink && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowStripeLink(false)} />
            <div className="relative bg-white rounded-xl shadow-lg w-full max-w-md mx-4">
              <div className="px-6 pt-6 pb-4">
                <h2 className="text-lg font-bold text-gray-900 mb-3">Stripe Redirect Link</h2>
                <div className="flex items-start gap-2">
                  <textarea
                    readOnly
                    rows={3}
                    value={activePlan.stripeRedirectLink}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                    style={{ fieldSizing: 'content' } as React.CSSProperties}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(activePlan.stripeRedirectLink!);
                      setStripeLinkCopied(true);
                      setTimeout(() => setStripeLinkCopied(false), 2000);
                    }}
                    className="shrink-0 px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {stripeLinkCopied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-end px-6 py-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowStripeLink(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        {showStartDateModal && planBusiness && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowStartDateModal(false)} />
            <div className="relative bg-white rounded-xl shadow-lg w-full max-w-sm mx-4">
              <div className="px-6 pt-6 pb-4">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Shop Start Date</h2>
                <input
                  type="date"
                  value={startDateInput}
                  onChange={(e) => setStartDateInput(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowStartDateModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!startDateInput}
                  onClick={async () => {
                    await updateShopStartDate(planBusiness.id, startDateInput);
                    setShowStartDateModal(false);
                    fetchPlanData();
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
        {showCollisionWindowModal && planBusiness && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowCollisionWindowModal(false)} />
            <div className="relative bg-white rounded-xl shadow-lg w-full max-w-sm mx-4">
              <div className="px-6 pt-6 pb-4">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Collision Window</h2>
                <label className="block text-sm font-medium text-gray-700 mb-1">Days</label>
                <input type="number" value={collisionWindowInput} onChange={(e) => setCollisionWindowInput(Number(e.target.value))} min={0} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" />
              </div>
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
                <button type="button" onClick={() => setShowCollisionWindowModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
                <button type="button" onClick={async () => { await updateCollisionWindow(planBusiness.id, collisionWindowInput); setShowCollisionWindowModal(false); fetchPlanData(); }} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors">Save</button>
              </div>
            </div>
          </div>
        )}
        {showScoringNote && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowScoringNote(null)} />
            <div className="relative bg-white rounded-xl shadow-lg w-full max-w-md mx-4 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-3">Scoring Suggestion</h2>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{showScoringNote}</p>
              <div className="mt-4 flex justify-end">
                <button onClick={() => setShowScoringNote(null)} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Close</button>
              </div>
            </div>
          </div>
        )}
        {showAgentModeModal && planBusiness && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowAgentModeModal(false)} />
            <div className="relative bg-white rounded-xl shadow-lg w-full max-w-sm mx-4">
              <div className="px-6 pt-6 pb-4">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Agent Mode</h2>
                <select value={agentModeInput} onChange={(e) => setAgentModeInput(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm">
                  <option value="property">Per Property</option>
                  <option value="client">Client-wide</option>
                </select>
              </div>
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
                <button type="button" onClick={() => setShowAgentModeModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
                <button type="button" onClick={async () => { await updateAgentMode(planBusiness.id, agentModeInput); setShowAgentModeModal(false); fetchPlanData(); }} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors">Save</button>
              </div>
            </div>
          </div>
        )}
      </IonContent>
    </IonPage>
  );
}
